import React, { useState, forwardRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import CustomLink from "@gc-npm/components.custom-link";
import SceneLayer from "./scene-layer";
import useScreenType from "./screen-type";
import style from "./style";
const AnimatedBanner = forwardRef((props, ref) => {
  const {
    userName = "",
    canvas = {
      maxWidth: "1480px",
      backgroundColor: "#FFF8F6",
      boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
      borderRadius: "16px",
      heights: { desktop: "180px", tablet: "150px", mobile: "240px" },
    },
    scenes = [],
    activeSceneIndex = null,
    deviceView = null,
    fitParentContainer = false,
    isMiniPlaying = false,
    isPlaygroundResponsive = false,
  } = props;
  const [animationKey] = useState(0);
  const screenType = useScreenType(deviceView);

  if (!canvas || !scenes) return null;

  const scenesProcesadasConUsuario = scenes.map((scene) => {
    if (scene.elements && scene.elements["Text-username"] && userName) {      

      let p= userName.trim().split(" ")[0];
      let n = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      return {
        ...scene,
        elements: {
          ...scene.elements,
          "Text-username": {
            ...scene.elements["Text-username"],
            text: n,           
          }
        },
      };
    
    }
    
    return scene;
  });

  const activeBreakpoint = screenType === "responsive" ? "desktop" : screenType;
  const screenTypeReady = Boolean(screenType);
  const desktopHeight = canvas.heights?.desktop || "auto";
  const tabletHeight = canvas.heights?.tablet || desktopHeight;
  const mobileHeight = canvas.heights?.mobile || tabletHeight;
  const currentHeight = screenTypeReady
    ? canvas.heights?.[activeBreakpoint] || "auto"
    : undefined;

  const elementosEscenaFondo = scenes[0]?.elements;
  const imagenActiva = elementosEscenaFondo?.[`Image-${activeBreakpoint}`];

  const widthNativo = parseInt(imagenActiva?.width, 10);
  const heightNativo = parseInt(imagenActiva?.height, 10);

  let aspectRatioDinamico = "1480 / 200";

  if (widthNativo && heightNativo) {
    aspectRatioDinamico = `${widthNativo} / ${heightNativo}`;
  } else {
    const fallbacks = {
      desktop: "1480 / 200",
      tablet: "1270 / 310",
      mobile: "768 / 340",
    };
    aspectRatioDinamico = fallbacks[activeBreakpoint] || "1480 / 200";
  }
  const canvasStyle = {
    display: "block",
    textDecoration: "none",
    width: "100%",
    maxWidth: fitParentContainer ? "100%" : canvas.maxWidth,
    aspectRatio: screenType === "responsive" ? "auto" : aspectRatioDinamico,
    height: screenType === "responsive" ? "100%" : "auto",
    margin: "auto 0px",
    backgroundColor: canvas.backgroundColor,
    position: "relative",
    overflow: "hidden",
    boxShadow: canvas.boxShadow,
    borderRadius: canvas.borderRadius,
    cursor: canvas["CustomLink-1"] ? "pointer" : "default",
    boxSizing: "border-box",
    visibility: screenTypeReady ? "visible" : "hidden",
  };

  const bannerContent = screenTypeReady
    ? scenesProcesadasConUsuario.map((scene, index) => {
        const isEditing = activeSceneIndex !== null;
        const isActiveScene = index === activeSceneIndex;
        let sceneProcesada = scene;
        if (isMiniPlaying && scene.animateEffect) {
          sceneProcesada = {
            ...scene,
            animateEffect: scene.animateEffect.replace(
              /(\s\d+(?:\.\d+)?s)$/,
              " 0s"
            ),
          };
        }
        const animationWrapper = (
          <SceneLayer
            scene={sceneProcesada}
            index={index}
            activeBreakpoint={activeBreakpoint}
            animationKey={animationKey}
            activeSceneIndex={activeSceneIndex}
          />
        );
        if (isEditing) {
          return (
            <div
              key={`edit-wrapper-${index}`}
              className={
                isActiveScene
                  ? "scene-editable-active"
                  : "scene-background-veil"
              }
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: isActiveScene ? "auto" : "none",
                zIndex: index * 10,
              }}
            >
              {animationWrapper}
            </div>
          );
        }
        return animationWrapper;
      })
    : null;
  const isEditingInPlayground = activeSceneIndex !== null && !isMiniPlaying;
  const shouldRenderLink = canvas["CustomLink-1"] && !isEditingInPlayground;
  const bannerClassName = [
    activeBreakpoint,
    "animated-banner",
    isPlaygroundResponsive ? "is-elastic" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <>
      {shouldRenderLink ? (
        <CustomLink
          {...canvas["CustomLink-1"]}
          ref={ref}
          style={{
            display: "block",
            width: "100%",
            height: isPlaygroundResponsive ? "100%" : "auto",
          }}
        >
          <div
            style={canvasStyle}
            className={bannerClassName}
            data-editing={isEditingInPlayground}
          >
            {bannerContent}
          </div>
        </CustomLink>
      ) : (
        <div
          style={{
            ...canvasStyle,
            cursor: isEditingInPlayground
              ? "default"
              : canvas["CustomLink-1"]
                ? "pointer"
                : "default",
          }}
          ref={ref}
          id="animated-banner-root"
          className={bannerClassName}
          data-editing={isEditingInPlayground}
        >
          {bannerContent}
        </div>
      )}

      <style jsx>{style}</style>
      <style jsx>{`
        .animated-banner {
          height: ${canvas.heights?.desktop || "203px"};
        }
        @media screen and (max-width: 1269px) {
          .animated-banner {
            height: ${canvas.heights?.tablet || "311px"};
          }
        }
        @media screen and (max-width: 768px) {
          .animated-banner {
            height: ${canvas.heights?.mobile || "240px"};
          }
        }

        @supports (aspect-ratio: 1 / 1) {
          .animated-banner {
            height: auto !important;
          }
        }
        .animated-banner {
          height: ${desktopHeight};
        }
        @media screen and (max-width: 1269px) {
          .animated-banner {
            height: ${tabletHeight};
          }
        }
        @media screen and (max-width: 768px) {
          .animated-banner {
            height: ${mobileHeight};
          }
        }
      `}</style>
      <style jsx global>{`
        .animated-banner.is-elastic {
          height: 100% !important;
        }
        .is-elastic,
        [id="animated-banner-root"].is-elastic {
          height: 100% !important;
          display: block !important;
        }

        #animated-banner-root,
        .animated-banner {
          perspective: 1200px !important;
        }
        .banner-scene-layer {
          opacity: 0;
          animation: var(--scene-animation);
          animation-fill-mode: both;
          transform-style: preserve-3d !important;
          backface-visibility: hidden !important;
          -webkit-backface-visibility: hidden !important;
        }
        .banner-scene-layer.non-animated {
          opacity: 1;
        }
        [data-editing="true"] .banner-scene-layer {
          opacity: 1 !important;
          animation: none !important;
          visibility: visible !important;
        }
        [data-editing="true"] .scene-background-veil {
          opacity: 0.35 !important;
          filter: grayscale(40%) contrast(95%);
        }
        [data-editing="true"] .scene-editable-active {
          opacity: 1 !important;
        }
        .banner-scene-layer img,
        .banner-scene-layer picture {
          opacity: inherit !important;
        }

        @keyframes rotateBlack {
          0%,
          35% {
            transform: translateY(0);
            opacity: 1;
            filter: blur(0px);
          }

          45% {
            transform: translateY(-30px);
            opacity: 0;
            filter: blur(2px);
          }
          50%,
          100% {
            transform: translateY(-30px);
            opacity: 0;
          }
        }

        @keyframes rotateName {
          0%,
          35% {
            transform: translateY(30px);
            opacity: 0;
            filter: blur(2px);
            text-shadow: none;
          }
          45% {
            transform: translateY(30px);
            opacity: 0;
            filter: blur(2px);
            text-shadow: none;
          }

          55% {
            transform: translateY(0);
            opacity: 1;
            filter: blur(0px);
            color: #ffffff;
            text-shadow:
              0 0 5px #fff,
              0 0 10px #00a4e4,
              0 0 20px #00a4e4,
              0 0 40px #00a4e4;
          }

          65% {
            text-shadow:
              0 0 7px #fff,
              0 0 15px #00a4e4,
              0 0 30px #00a4e4,
              0 0 60px #00a4e4;
          }

          75% {
            text-shadow:
              0 0 2px #fff,
              0 0 5px #00a4e4,
              0 0 10px #00a4e4;
          }

          85%,
          100% {
            transform: translateY(0);
            opacity: 1;
            filter: blur(0px);
            color: #ffffff;
            text-shadow: none;
          }
        }

        @keyframes neonFriday {
          0%,
          45% {
            text-shadow: none;
            opacity: 1;
          }

          55% {
            text-shadow:
              0 0 5px #fff,
              0 0 10px #00a4e4,
              0 0 20px #00a4e4,
              0 0 40px #00a4e4;
            opacity: 1;
          }

          65% {
            text-shadow:
              0 0 7px #fff,
              0 0 15px #00a4e4,
              0 0 30px #00a4e4,
              0 0 60px #00a4e4;
          }

          75% {
            text-shadow:
              0 0 2px #fff,
              0 0 5px #00a4e4,
              0 0 10px #00a4e4;
          }

          85%,
          100% {
            text-shadow: none;
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(15px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInDown {
          0% {
            opacity: 0;
            transform: translateY(-15px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          60% {
            opacity: 0.9;
            transform: scale(1.04);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes zoomIn {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(100px) scale(0.6);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes slideInLeft {
          0% {
            opacity: 0;
            transform: translateX(-100px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes revealText {
          0% {
            opacity: 0;
            clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
          }
          1% {
            opacity: 1;
          }
          100% {
            opacity: 1;
            clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
          }
        }
        @keyframes flipInX {
          0% {
            transform: perspective(400px) rotateX(90deg);
            opacity: 0;
          }
          40% {
            opacity: 0.5;
            transform: perspective(400px) rotateX(-10deg);
          }
          70% {
            transform: perspective(400px) rotateX(10deg);
          }
          100% {
            transform: perspective(400px) rotateX(0deg);
            opacity: 1;
          }
        }
        @keyframes slideInUp {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.98);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes textFocusIn {
          0% {
            opacity: 0;
            filter: blur(12px);
          }
          100% {
            opacity: 1;
            filter: blur(0px);
          }
        }
        @keyframes elementPulse {
          0% {
            opacity: 0;
            transform: scale(1) rotate(0deg);
          }
          1%,
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.05) rotate(0.5deg);
          }
        }
        @keyframes shimmerCTA {
          0% {
            opacity: 0;
            filter: brightness(1);
            transform: scale(1);
          }
          1%,
          100% {
            opacity: 1;
            filter: brightness(1);
            transform: scale(1);
          }
          50% {
            opacity: 1;
            filter: brightness(1.25);
            transform: scale(1.02);
          }
        }
        @keyframes bounceMarker {
          0% {
            opacity: 0;
            transform: translateY(0);
          }
          1%,
          20%,
          50%,
          80%,
          100% {
            opacity: 1;
            transform: translateY(0);
          }
          40% {
            opacity: 1;
            transform: translateY(-8px);
          }
          60% {
            opacity: 1;
            transform: translateY(-4px);
          }
        }
        @keyframes glitchAttention {
          0% {
            opacity: 0;
            transform: translate(0, 0);
          }
          1%,
          100% {
            opacity: 1;
            transform: translate(0, 0);
          }
          20% {
            opacity: 1;
            transform: translate(-2px, 2px);
          }
          40% {
            opacity: 1;
            transform: translate(-2px, -2px);
          }
          60% {
            opacity: 1;
            transform: translate(2px, 2px);
          }
          80% {
            opacity: 1;
            transform: translate(2px, -2px);
          }
        }
        @keyframes heartbeat {
          0% {
            opacity: 0;
            transform: scale(1);
          }
          1%,
          28%,
          70%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          14% {
            opacity: 1;
            transform: scale(1.1);
          }
          42% {
            opacity: 1;
            transform: scale(1.15);
          }
        }
        @keyframes floatLoop {
          0% {
            opacity: 0;
            transform: translateY(0px);
          }
          1%,
          100% {
            opacity: 1;
            transform: translateY(0px);
          }
          50% {
            opacity: 1;
            transform: translateY(-6px);
          }
        }
        @keyframes zoomInBg {
          0% {
            opacity: 0;
            transform: scale(1);
          }
          1%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.08);
          }
        }
        @keyframes zoomOutBg {
          0% {
            opacity: 0;
            transform: scale(1.1);
          }
          1%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.03);
          }
        }
        @keyframes pulseAttention {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.08);
            filter: drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.4));
          }
        }
      `}</style>
    </>
  );
});

AnimatedBanner.propTypes = {
  canvas: PropTypes.shape({
    maxWidth: PropTypes.string,
    backgroundColor: PropTypes.string,
    boxShadow: PropTypes.string,
    borderRadius: PropTypes.string,
    heights: PropTypes.shape({
      desktop: PropTypes.string,
      tablet: PropTypes.string,
      mobile: PropTypes.string,
    }),
  }),
  scenes: PropTypes.array,
  activeSceneIndex: PropTypes.number,
  deviceView: PropTypes.string,
  fitParentContainer: PropTypes.bool,
  isMiniPlaying: PropTypes.bool,
  isPlaygroundResponsive: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  userName: state.main.context.userName,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AnimatedBanner);
